import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Check, Loader2, ShieldCheck, Landmark } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { Toaster } from "../components/ui/toaster";
import { ValuationProvider } from "../components/ValuationDialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Content() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [state, setState] = useState("loading");
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState({ bank_account_name: "", bank_name: "", sort_code: "", account_number: "" });
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    axios.get(`${API}/terms-acceptances/${id}/summary`)
      .then((r) => { setInfo(r.data); setState("ok"); })
      .catch(() => setState("invalid"));
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/terms-acceptances/${id}/bank-details`, form);
      setDone(true);
    } catch (err) {
      toast({ title: "Please check your details", description: err.response?.data?.detail || "Something went wrong — try again.", variant: "destructive" });
    } finally { setSaving(false); }
  };

  return (
    <div className="App">
      <Seo title="Add your rent payment details | New Space" description="Securely add the bank account your rent should be paid into." path="/landlords/terms/bank" noindex />
      <Header />
      <section className="relative overflow-hidden bg-[#16323f] pt-32 pb-14 lg:pt-40">
        <div className="mx-auto max-w-[760px] px-5 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#d8b074]">New Space</p>
          <h1 className="mt-3 font-display text-4xl font-700 uppercase tracking-wide text-white lg:text-5xl">Where to pay your rent</h1>
          {info && <p className="mt-3 text-white/80" data-testid="bank-details-intro">For {info.name}{info.property_address && <> &middot; {info.property_address}</>}</p>}
        </div>
      </section>
      <section className="bg-[#faf7f0] py-14 lg:py-20">
        <div className="mx-auto max-w-[760px] px-5">
          {state === "loading" ? (
            <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#35a4a4]" size={32} /></div>
          ) : state === "invalid" ? (
            <div className="rounded-2xl border border-[#e5ddd0] bg-white p-10 text-center shadow-lg" data-testid="bank-details-invalid">
              <h2 className="font-display text-2xl font-600 text-[#16323f]">This link isn&rsquo;t valid</h2>
              <p className="mx-auto mt-3 max-w-md text-sm text-[#5b5450]">We couldn&rsquo;t find a signed Terms of Business for this link. Please call us on 020 7275 2040.</p>
            </div>
          ) : done ? (
            <div className="rounded-2xl border border-[#e5ddd0] bg-white p-10 text-center shadow-lg" data-testid="bank-details-success">
              <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-[#35a4a4] text-white"><Check size={34} /></div>
              <h2 className="mt-5 font-display text-2xl font-600 text-[#16323f]">Thank you — all received</h2>
              <p className="mx-auto mt-3 max-w-md text-sm text-[#5b5450]">Your payment details have been securely added to your signed Terms of Business. Our accounts team will use them for your rent payments.</p>
              <button onClick={() => navigate("/")} data-testid="bank-details-home" className="mt-7 rounded-full bg-[#16323f] px-9 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#ec008c]">Back to homepage</button>
            </div>
          ) : (
            <form onSubmit={submit} className="rounded-2xl border border-[#e5ddd0] bg-white p-7 shadow-lg lg:p-9" data-testid="bank-details-form">
              <div className="flex items-start gap-4">
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-[#16323f] text-[#d8b074]"><Landmark size={20} /></span>
                <div>
                  <h2 className="font-display text-xl font-600 uppercase tracking-wide text-[#16323f]">Your rent payment account</h2>
                  <p className="mt-1 text-sm text-[#7a716a]">The UK bank account we should pay your rent into. {info?.has_bank_details ? "You already gave us details — submitting again will replace them." : "You skipped this when signing, so we just need it to complete your file."}</p>
                </div>
              </div>
              <div className="mt-7 grid gap-5 sm:grid-cols-2">
                <div className="space-y-1.5"><Label htmlFor="b-name">Account holder name *</Label><Input id="b-name" data-testid="bank-account-name" required value={form.bank_account_name} onChange={(e) => set("bank_account_name", e.target.value)} placeholder="As it appears on the account" /></div>
                <div className="space-y-1.5"><Label htmlFor="b-bank">Bank name</Label><Input id="b-bank" data-testid="bank-name" value={form.bank_name} onChange={(e) => set("bank_name", e.target.value)} placeholder="e.g. Barclays" /></div>
                <div className="space-y-1.5"><Label htmlFor="b-sort">Sort code *</Label><Input id="b-sort" data-testid="bank-sort-code" required inputMode="numeric" value={form.sort_code} onChange={(e) => set("sort_code", e.target.value)} placeholder="00-00-00" /></div>
                <div className="space-y-1.5"><Label htmlFor="b-acc">Account number *</Label><Input id="b-acc" data-testid="bank-account-number" required inputMode="numeric" value={form.account_number} onChange={(e) => set("account_number", e.target.value)} placeholder="12345678" /></div>
              </div>
              <div className="mt-4 flex items-start gap-2 text-xs text-[#7a716a]"><ShieldCheck size={14} className="mt-0.5 shrink-0 text-[#35a4a4]" />Your details are encrypted before being stored and only used to pay your rent.</div>
              <button type="submit" disabled={saving} data-testid="bank-details-submit" className="mt-7 flex w-full items-center justify-center gap-2 rounded-full bg-[#ec008c] py-4 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#c60074] disabled:opacity-70">
                {saving ? <><Loader2 size={16} className="animate-spin" /> Saving&hellip;</> : "Save my payment details"}
              </button>
            </form>
          )}
        </div>
      </section>
      <Footer />
      <Toaster />
    </div>
  );
}

export default function TermsBankDetails() {
  return <ValuationProvider><Content /></ValuationProvider>;
}
